// Operator release of the guest's own "בעריכה" hold (Swingies / MiniMove): the guest goes back to the event Home.
//
// * The ONLY trigger is the server's observed reason for THIS tab's stored token: `mine.code == "revoked"` on the
//   held-list read, or `410 claim_revoked` on renew / acquire-swap / approve. Expiry, a gone token, network
//   failures, 5xx and processing never navigate from here (their existing handling stays).
// * Ending is synchronous and latched (the flow-expiry latch): no acquire/approve can start in this visit after
//   it, and arriving at Home arms a new flow. Home's fresh-guest start clears the selection and edit state.
// * Past the face gallery (body gallery, edit, result) nothing else watches the hold, so useAdminReleaseWatch
//   reads it every HELD_POLL_MS while visible (plus focus / online / pageshow). Not while an approve is in
//   flight (the flow is paused; the approve's own answer decides) and never without a stored token (shared /
//   reusable events hold nothing, so parallel use is unaffected).
// * A reply counts only while the watcher is mounted, the event visit is the same and the stored token is still
//   the one it asked about (Back released it, a newer hold replaced it -> ignored).
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getFaceClaimEvent } from "./faceClaimEvent";
import { clearClaimState, clearFlowState, fetchHeld, readClaimState } from "./faceClaimClient";
import { isFlowEnded, isFlowPaused, latchFlowEnded } from "./useFlowInactivity";
import { HELD_POLL_MS } from "./useFaceClaim";
import { showAlert } from "../../../generalUtils";

export const MSG_ADMIN_RELEASED = "מנהל האירוע שחרר את הראש שבחרתם. אפשר להתחיל מחדש";
export const ADMIN_RELEASE_TOAST_MS = 8000;
// Flow pages after the face gallery (the gallery's own useFaceClaim watches the hold there).
export const WATCHED_PAGES = Object.freeze(["body-gallery", "edit-your-character", "result"]);
const READ_TIMEOUT_MS = 8000;

// Ends the active visit's flow and navigates Home with the explanation. Returns false when there is nothing
// to end (no current event, or the flow already ended/expired: no second toast or navigation).
export const returnHomeAfterAdminRelease = (navigate) => {
  const event = getFaceClaimEvent();
  if (!event || !event.isCurrent() || isFlowEnded(event)) return false;
  // The server already freed the heads (the token is revoked): nothing to release.
  clearClaimState(event.eventName);
  clearFlowState(event.eventName);
  latchFlowEnded(event);
  showAlert("err", MSG_ADMIN_RELEASED, ADMIN_RELEASE_TOAST_MS);
  navigate(event.homeRoute, { replace: true });
  return true;
};

export const useAdminReleaseWatch = ({ enabled, page }) => {
  const navigate = useNavigate();
  const watched = Boolean(enabled) && WATCHED_PAGES.includes(page);

  useEffect(() => {
    if (!watched) return undefined;
    const event = getFaceClaimEvent();
    if (!event) return undefined;
    let disposed = false;
    let inflight = false;
    const storedToken = () => (readClaimState(event.eventName) || {}).token || null;

    const check = async () => {
      if (disposed || inflight || !event.isCurrent()) return;
      if (typeof document !== "undefined" && document.hidden) return;
      if (isFlowPaused() || isFlowEnded(event)) return;
      const token = storedToken();
      if (!token) return;
      inflight = true;
      try {
        const result = await fetchHeld(event, token, { timeoutMs: READ_TIMEOUT_MS });
        if (disposed || !event.isCurrent() || storedToken() !== token) return;
        const mine = result.ok && result.body && result.body.mine;
        if (mine && mine.code === "revoked") returnHomeAfterAdminRelease(navigate);
      } finally {
        inflight = false;
      }
    };

    check();
    const poll = setInterval(check, HELD_POLL_MS);
    const onVisible = () => check();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    window.addEventListener("focus", onVisible);
    window.addEventListener("online", onVisible);
    return () => {
      disposed = true;
      clearInterval(poll);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("online", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watched, page]);
};
